/**
 * PaymentSummaryCard — shared component showing paid/total/remaining with progress bar.
 *
 * Used by Success.tsx and ViewSubscription.tsx.
 * Hidden entirely when totalSubscription is 0.
 */
import React from 'react';
import { formatCurrency } from '../../utils/subscriptionHelpers';
import { AdjustedAmount } from './AdjustedAmount';
import './styles/PaymentSummaryCard.css';

export interface PaymentSummaryCardProps {
  totalPaid: number;
  totalSubscription: number;
  /** Effective amount paid after applying per-row adjustments. Shown in the Paid column. */
  adjustedPaid?: number | null;
  /** Effective subscription total after all adjustments. Shown below the Total row. */
  adjustedTotal?: number | null;
  remaining: number;
  progressPct: number;
  pendingInstallments?: number;
  title?: string;
}

export const PaymentSummaryCard: React.FC<PaymentSummaryCardProps> = ({
  totalPaid,
  totalSubscription,
  adjustedPaid,
  adjustedTotal,
  remaining,
  progressPct,
  pendingInstallments,
  title = 'Payment Summary',
}) => {
  if (totalSubscription === 0) return null;

  const paidAdj = adjustedPaid != null ? adjustedPaid - totalPaid : 0;
  const totalAdj = adjustedTotal != null ? adjustedTotal - totalSubscription : 0;

  return (
    <div className="payment-summary">
      <div className="payment-summary__card">
        <h3 className="payment-summary__title">{title}</h3>
        {/* Paid / Total row */}
        <div className="payment-summary__top-row">
          <div className="payment-summary__paid-col">
            <span className="payment-summary__paid">Paid</span>
            <AdjustedAmount
              original={totalPaid}
              adj={paidAdj}
              align="start"
              originalClassName="payment-summary__paid-amount"
            />
          </div>
          <div className="payment-summary__total-col">
            <span className="payment-summary__total">Total</span>
            <AdjustedAmount
              original={totalSubscription}
              adj={totalAdj}
              originalClassName="payment-summary__total-amount"
            />
          </div>
        </div>

        {/* Progress bar */}
        <div className="payment-summary__progress-bar">
          <div
            className="payment-summary__progress-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Remaining info */}
        {remaining > 0 && (
          <p className="payment-summary__remaining">
            {formatCurrency(remaining)} remaining
            {pendingInstallments != null && pendingInstallments > 0 && (
              <> · {pendingInstallments} installment{pendingInstallments > 1 ? 's' : ''} left</>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentSummaryCard;
